import { useMemo, useRef } from "react"

/**
 * Groups a flat list by framework (first-seen order) and exposes a
 * scroll-to-section helper — shared state between `FrameworkChips` (rendered
 * full-width, outside the left/right split) and `FrameworkGroupedList`
 * (rendered inside the left panel), so a page can compose the two in
 * whatever layout it needs instead of them being tied together.
 *
 * `fallbackLabel` is required (not defaulted) because this hook has no
 * access to `useTranslation` — callers must pass an already-translated
 * label (e.g. `translate("MC_QP_FRAMEWORK_OTHER")`).
 */
export function useFrameworkGroups<T>(
  items: T[],
  frameworkOf: (item: T) => string | null,
  fallbackLabel: string,
) {
  const groups = useMemo(() => {
    const order: string[] = []
    const map = new Map<string, T[]>()
    items.forEach((item) => {
      const fw = frameworkOf(item) || fallbackLabel
      if (!map.has(fw)) {
        map.set(fw, [])
        order.push(fw)
      }
      map.get(fw)?.push(item)
    })
    return order.map((fw) => [fw, map.get(fw) ?? []] as const)
  }, [items, frameworkOf, fallbackLabel])

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const registerSection =
    (framework: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[framework] = el
    }

  const jumpTo = (framework: string) => {
    sectionRefs.current[framework]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  return { groups, registerSection, jumpTo }
}
